/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
  memo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tutoringAPILogin, tutoringAPILogout } from '../../utils/Network';

type Role = 'ADMIN' | 'SUPERVISOR' | 'TEACHER' | 'SUPER_USER';

export type User = {
  email: string;
  username: string;
  name: string;
  lastName: string;
  roles: Role[];
};

type iesSession = {
  tokenId: string;
  user: {
    email: string;
    id: string;
  };
};

type DecodedJWTToken = {
  user: User;
  roles: string[];
  iat: number;
  exp: number;
  iesSession: iesSession;
};

interface UserContextProps {
  user: any;
  userToken: string;
  loading: boolean;
  isLoggedIn: boolean;
  init: boolean;
  error: boolean;
  hasRole: (role: string | string[]) => boolean;
  login: () => void;
  logout: () => void;
}

const emptyContext: UserContextProps = {
  user: null,
  userToken: '',
  isLoggedIn: false,
  loading: false,
  init: false,
  error: false,
  hasRole: () => {
    return true;
  },
  login: () => {},
  logout: () => {},
};

const UserContext = createContext(emptyContext);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = memo(({ children }: UserProviderProps) => {
  // Redirect to the external OAuth provider
  const iesUrl = process.env.REACT_APP_IES_AUTH_URL;

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [jwtToken, setJwtToken] = useState<string>('');

  // State Tracking
  const [init, setInit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const hasUseEffectExecuted = useRef(false);

  const resetToken = useCallback(() => {
    console.log('Reset Token');
    localStorage.removeItem('session_token');
    localStorage.removeItem('user_info');
    setJwtToken('');
    setUser(null);
  }, []);

  const saveAuthToken = useCallback(
    (token: string) => {
      try {
        const decodedJWTToken = jwtDecode<DecodedJWTToken>(token);

        // Check if the token is expired
        if (decodedJWTToken.exp * 1000 > Date.now()) {
          saveUserInfo(decodedJWTToken);

          localStorage.setItem('session_token', token);
          setJwtToken(token);
          setInit(true);
        } else {
          console.info('Jwt expired');
          resetToken();
          navigate('/login');
        }
      } catch (error) {
        console.error('Invalid token found', error);
        resetToken();
      }
    },
    [resetToken, navigate],
  );

  const saveUserInfo = (decodedJWTToken: DecodedJWTToken) => {
    const userEmail: string = decodedJWTToken.iesSession.user.email;

    const userInfo = {
      name: 'test',
      lastName: 'lastName',
      username: 'test',
      email: userEmail,
      roles: decodedJWTToken.roles,
    };

    setUser(userInfo as User);
    localStorage.setItem('user_info', JSON.stringify(userInfo));
  };

  // SINGLE INITIALIZATION EFFECT
  useEffect(() => {
    if (hasUseEffectExecuted.current) {
      return;
    }
    hasUseEffectExecuted.current = true;

    const code = searchParams.get('code');
    const routingRegion = searchParams.get('x-routingregion');

    const initialize = async () => {
      // SCENARIO 1: Handle the OAuth callback
      if (code && routingRegion) {
        try {
          const loginResponse = await tutoringAPILogin(code, routingRegion);
          saveAuthToken(loginResponse.jwt_token);
          navigate('/', { replace: true });
        } catch (e) {
          console.error('Error during OAuth callback', e);
          setError(true);
          setInit(true);
          setLoading(false);
          navigate('/login', { replace: true });
        }
      } else {
        // SCENARIO 2: Check for an existing session token
        const localToken = localStorage.getItem('session_token');
        if (localToken) {
          saveAuthToken(localToken);
        } else {
          setInit(true);
          navigate('/login');
        }
      }
      setLoading(false);
    };

    initialize();
  }, [saveAuthToken, searchParams, navigate]);

  const login = useCallback(() => {
    if (iesUrl) {
      window.location.href = iesUrl;
    } else {
      setError(true);
      console.error('No ies redirect url defined');
    }
  }, [iesUrl]);

  const logout = useCallback(async () => {
    setError(false);
    console.log('Logout function');

    try {
      const jwtToken = localStorage.getItem('session_token');
      if (jwtToken !== null) {
        await tutoringAPILogout(jwtToken);
      }
      setUser(null);
      setJwtToken('');
      setInit(false);
      localStorage.removeItem('session_token');
      localStorage.removeItem('user_info');
      navigate('/login');
    } catch (e) {
      setError(true);
      console.error('Error during logout', e);
    }
  }, [navigate]);

  const hasRole = useCallback(
    (role: string | string[]): boolean => {
      const requestedRoles = Array.isArray(role) ? role : [role];
      const upperCaseRoles = requestedRoles.map((r) => r.toUpperCase());

      if (!user || !user.roles) {
        return false;
      }

      const userRolesUpperCase = user.roles.map((r: string) =>
        typeof r === 'string' ? r.toUpperCase() : r,
      );

      for (const r of upperCaseRoles) {
        if (userRolesUpperCase.includes(r)) {
          return true;
        }
      }

      return false;
    },
    [user],
  );

  const value = useMemo(
    () => ({
      user: user,
      userToken: jwtToken,
      init: init,
      loading: loading,
      isLoggedIn: !!user,
      error: error,
      hasRole: hasRole,
      login: login,
      logout: logout,
    }),
    [user, jwtToken, loading, hasRole, login, logout, error, init],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
});

// fix for eslint:
UserProvider.displayName = 'UserProvider';

export const useUserContext = () => useContext(UserContext);
